import { sumTotalTimeDays } from './port-database-column-totals.util';

export interface DatabaseSummary {
  totalPortCalls: number;
  totalDays: number;
  totalCPConsumption: number;
  totalActualConsumption: number;
  totalVoyages: number;
  cpOverall: number;
  designOverall?: number;
  totalCpOverallMt: number;
  totalDesignOverallMt: number;
}

const EMPTY_SUMMARY: DatabaseSummary = {
  totalPortCalls: 0,
  totalDays: 0,
  totalCPConsumption: 0,
  totalActualConsumption: 0,
  totalVoyages: 0,
  cpOverall: 0,
  designOverall: 0,
  totalCpOverallMt: 0,
  totalDesignOverallMt: 0,
};

export function toSafeNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const cleaned = String(value).replace(/,/g, '').trim();
  if (!cleaned) {
    return 0;
  }
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function roundSummaryDisplay(value: number): number {
  return Math.round(toSafeNumber(value));
}

export function formatSummaryDisplay(value: number): string {
  return roundSummaryDisplay(value).toLocaleString('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

export function getVoyageKey(row: Record<string, unknown>): string {
  const imo = String(row['vessel_imo'] ?? row['imo'] ?? '').trim();
  const voyage = String(row['voyage_number'] ?? row['voy_num'] ?? '').trim();
  const vessel = String(row['vessel_name'] ?? '').trim().toLowerCase();
  return [imo || vessel, voyage].filter(Boolean).join('|');
}

export function getPortCallKey(row: Record<string, unknown>): string {
  const voyageKey = getVoyageKey(row);
  const port = String(row['port'] ?? '').trim().toLowerCase();
  const arrival = String(row['arrival_date_and_time'] ?? '').trim();
  const operation = String(row['operations'] ?? '').trim().toLowerCase();
  return [voyageKey, port, arrival, operation].filter(Boolean).join('|');
}

export function getActualConsumptionMt(row: Record<string, unknown>): number {
  const actual = row['actual_consumptions_mt'] as Record<string, unknown> | undefined;
  return (
    toSafeNumber(actual?.['actual_consumptions_mt_hsfo']) +
    toSafeNumber(actual?.['actual_consumptions_mt_lsgo']) +
    toSafeNumber(actual?.['actual_consumptions_mt_vlsfo'])
  );
}

export function getCpWarrantedConsumptionMt(row: Record<string, unknown>): number {
  const cp = row['cp_warranted_consumptions_mt'] as Record<string, unknown> | undefined;
  return (
    toSafeNumber(cp?.['cp_warranted_consumptions_mt_hsfo']) +
    toSafeNumber(cp?.['cp_warranted_consumptions_mt_lsgo']) +
    toSafeNumber(cp?.['cp_warranted_consumptions_mt_vlsfo'])
  );
}

export function getDesignConsumptionMt(row: Record<string, unknown>): number {
  const design = (row['design_consumptions_mt_maneuvering_cargo_idle'] ??
    row['design_consumptions_mt']) as Record<string, unknown> | undefined;
  return (
    toSafeNumber(design?.['maneuvering_design_consumption_mt']) +
    toSafeNumber(design?.['cargo_ops_design_consumption_mt']) +
    toSafeNumber(design?.['idle_design_consumption_mt'])
  );
}

export function calculateTotalPortCalls(rows: Record<string, unknown>[]): number {
  return (rows ?? []).length;
}

export function calculateCpOverallPercent(
  totalCPConsumption: number,
  totalActualConsumption: number,
): number {
  const cp = toSafeNumber(totalCPConsumption);
  const actual = toSafeNumber(totalActualConsumption);
  if (cp <= 0) {
    return 0;
  }
  // Derived from filtered totals — not an average of row-level percentages.
  const percent = (actual / cp) * 100;
  return Number.isFinite(percent) ? percent : 0;
}

/** Display CP Overall as a whole-number percentage (e.g. 79%). */
export function formatCpOverallPercent(value: number): string {
  const percent = toSafeNumber(value);
  if (!Number.isFinite(percent) || percent <= 0) {
    return '0%';
  }
  return `${Math.round(percent)}%`;
}

export function calculateDesignOverallPercent(
  totalDesignConsumption: number,
  totalActualConsumption: number,
): number {
  const design = toSafeNumber(totalDesignConsumption);
  const actual = toSafeNumber(totalActualConsumption);
  if (design <= 0) {
    return 0;
  }
  const percent = (actual / design) * 100;
  return Number.isFinite(percent) ? percent : 0;
}

/** Display Design Consumption Overall as a whole-number percentage (e.g. 79%). */
export function formatDesignOverallPercent(value: number): string {
  const percent = toSafeNumber(value);
  if (!Number.isFinite(percent) || percent <= 0) {
    return '0%';
  }
  return `${Math.round(percent)}%`;
}

export function calculateUniqueVoyages(rows: Record<string, unknown>[]): number {
  if (!rows?.length) {
    return 0;
  }
  const keys = new Set<string>();
  for (const row of rows) {
    const key = getVoyageKey(row);
    if (key) {
      keys.add(key);
    }
  }
  return keys.size;
}

export function calculateDatabaseSummary(
  rows: Record<string, unknown>[] | null | undefined,
): DatabaseSummary {
  const source = rows ?? [];
  if (!source.length) {
    return { ...EMPTY_SUMMARY };
  }

  let totalCPConsumption = 0;
  let totalActualConsumption = 0;
  let totalDesignConsumption = 0;
  let totalCpOverallMt = 0;
  let totalDesignOverallMt = 0;

  for (const row of source) {
    totalCPConsumption += getCpWarrantedConsumptionMt(row);
    totalActualConsumption += getActualConsumptionMt(row);
    totalDesignConsumption += getDesignConsumptionMt(row);
    totalCpOverallMt += toSafeNumber(row['cp_overall_mt']);
    totalDesignOverallMt += toSafeNumber(row['design_overall_mt']);
  }

  const totalVoyages = calculateUniqueVoyages(source);
  const cpOverall = calculateCpOverallPercent(
    totalCPConsumption,
    totalActualConsumption,
  );
  const designOverall = calculateDesignOverallPercent(
    totalDesignConsumption,
    totalActualConsumption,
  );

  return {
    totalPortCalls: calculateTotalPortCalls(source),
    totalDays: sumTotalTimeDays(source),
    totalCPConsumption,
    totalActualConsumption,
    totalVoyages,
    cpOverall,
    designOverall,
    totalCpOverallMt,
    totalDesignOverallMt,
  };
}
